import { useRef, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Upload, FileCheck, Paperclip } from "lucide-react";
import { toast } from "sonner";

export const TIPOS_CERT = ["IFS Food", "BRCGS Food", "FSSC 22000", "ISO 22000", "GlobalG.A.P.",
  "Ecológico UE", "Halal", "Kosher", "Otro"];

const nuevaCert = () => ({ tipo: "IFS Food", numero_certificado: "", entidad_certificadora: "", fecha_caducidad: "", fichero_id: null, otro_texto: "" });

export default function CertsEditor({ value = [], onChange }) {
  const fileRefs = useRef({});
  const [subiendo, setSubiendo] = useState(null);
  const certs = value || [];
  const set = (i, patch) => onChange(certs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const add = () => onChange([...certs, nuevaCert()]);
  const remove = (i) => onChange(certs.filter((_, idx) => idx !== i));

  const subir = async (i, file) => {
    if (!file) return;
    setSubiendo(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/certificados", fd);
      set(i, { fichero_id: data.fichero_id });
      toast.success("Certificado adjuntado");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSubiendo(null); }
  };

  return (
    <div data-testid="certs-editor">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Certificaciones</Label>
        <Button type="button" size="sm" variant="outline" onClick={add} data-testid="cert-add"><Plus size={14} className="mr-1" />Añadir</Button>
      </div>
      {certs.length === 0 && <div className="text-xs text-[#0E2A47]/40 mb-2">Sin certificaciones registradas.</div>}
      <div className="space-y-3">
        {certs.map((c, i) => (
          <div key={i} className="rounded-md border border-[#E2E8F0] p-3" data-testid={`cert-row-${i}`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Select value={c.tipo} onValueChange={(v) => set(i, { tipo: v })}>
                <SelectTrigger className="h-9" data-testid={`cert-tipo-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{TIPOS_CERT.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="date" value={c.fecha_caducidad || ""} onChange={(e) => set(i, { fecha_caducidad: e.target.value })} data-testid={`cert-caducidad-${i}`} className="h-9" placeholder="Caducidad" />
              <Input value={c.numero_certificado || ""} onChange={(e) => set(i, { numero_certificado: e.target.value })} data-testid={`cert-numero-${i}`} className="h-9" placeholder="Nº certificado" />
              <Input value={c.entidad_certificadora || ""} onChange={(e) => set(i, { entidad_certificadora: e.target.value })} data-testid={`cert-entidad-${i}`} className="h-9" placeholder="Entidad certificadora" />
            </div>
            <div className="flex items-center justify-between mt-2">
              <input type="file" accept=".pdf" hidden ref={(el) => (fileRefs.current[i] = el)} onChange={(e) => subir(i, e.target.files[0])} />
              <Button type="button" size="sm" variant="ghost" onClick={() => fileRefs.current[i]?.click()} data-testid={`cert-upload-${i}`} className="text-[#0E2A47]/70">
                {c.fichero_id ? <><FileCheck size={14} className="mr-1 text-[#10B981]" />PDF adjunto</> : <><Paperclip size={14} className="mr-1" />{subiendo === i ? "Subiendo…" : "Adjuntar PDF"}</>}
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => remove(i)} data-testid={`cert-remove-${i}`} className="text-[#EF4444]"><Trash2 size={14} /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
